import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { StandardEvents } from '@shopify/events';

/** Pack chosen per product, so switching variants (which re-renders the section) keeps it. */
const chosenCount = new Map();

/**
 * Variant mix per product for packs of 2+: units per variant id, and whether the shopper
 * changed it (an untouched mix follows the variant picker).
 * @type {Map<string, { counts: Record<string, number>, manual: boolean }>}
 */
const mixState = new Map();

/**
 * "Buy More, Save More" for products with variants: the chosen pack sets the quantity of
 * whatever variant is selected in this section's product forms and shows the pack total.
 * Packs of 2+ can mix variants (e.g. 1 gray + 1 green); product-form.js asks mixItems() and
 * adds one line per variant. The automatic volume discounts count the whole product, so a
 * mixed pack gets the same pack price in cart and checkout.
 *
 * @extends Component
 */
class VariantPackPicker extends Component {
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    this.addEventListener('change', this.#handleChange, { signal: this.#abort.signal });
    this.addEventListener('click', this.#handleMixClick, { signal: this.#abort.signal });
    // A variant switch re-renders the price after this element updates; re-apply once it lands.
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal: this.#abort.signal,
    });
    this.#restore();
    // Product form and price may upgrade after this element; apply once everything is defined.
    requestAnimationFrame(() => this.#apply());
    customElements.whenDefined('quantity-selector-component').then(() => this.#apply());
  }

  updatedCallback() {
    super.updatedCallback();
    // The section re-rendered for another variant: keep the pack and show its new total.
    this.#restore();
    requestAnimationFrame(() => this.#apply());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
  }

  get #selected() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="radio"]:checked'));
  }

  #restore() {
    const count = chosenCount.get(this.dataset.productId);
    if (!count) return;
    const input = this.querySelector(`input[type="radio"][value="${count}"]`);
    if (input instanceof HTMLInputElement) input.checked = true;
  }

  /** @param {Event & { promise?: Promise<unknown> }} event */
  #handleProductSelect = (event) => {
    const reapply = () =>
      requestAnimationFrame(() => {
        this.#restore();
        this.#apply();
      });
    if (event.promise) event.promise.then(reapply, () => {});
    else reapply();
  };

  #handleChange = () => {
    const input = this.#selected;
    if (input) chosenCount.set(this.dataset.productId, input.value);
    this.#apply();
  };

  get #count() {
    return Number(this.#selected?.value) || 1;
  }

  /** @returns {HTMLElement[]} */
  get #mixRows() {
    return Array.from(this.querySelectorAll('[data-mix-row]'));
  }

  /**
   * Lines to add for a pack of 2+, one per variant with units; null for a single unit.
   * Called by product-form.js right before adding to cart.
   * @returns {{ variantId: string, quantity: number }[] | null}
   */
  mixItems() {
    if (this.#count < 2 || !this.#mixRows.length) return null;
    const { counts } = this.#mix();
    return Object.entries(counts)
      .filter(([, quantity]) => quantity > 0)
      .map(([variantId, quantity]) => ({ variantId, quantity }));
  }

  /** Current mix, kept adding up to the pack size. */
  #mix() {
    const count = this.#count;
    const rows = this.#mixRows.filter((row) => !row.hasAttribute('data-unavailable'));
    const ids = rows.map((row) => row.dataset.variantId ?? '');
    const selectedId = rows.find((row) => row.hasAttribute('data-selected'))?.dataset.variantId ?? ids[0];
    let state = mixState.get(this.dataset.productId ?? '');

    if (!state || !state.manual) {
      // Untouched: the whole pack is the variant chosen in the variant picker.
      state = { counts: Object.fromEntries(ids.map((id) => [id, id === selectedId ? count : 0])), manual: false };
    } else {
      for (const id of Object.keys(state.counts)) if (!ids.includes(id)) delete state.counts[id];
      for (const id of ids) state.counts[id] ??= 0;
      let total = Object.values(state.counts).reduce((sum, n) => sum + n, 0);
      // Pack size changed: grow the selected variant, shrink the others first.
      if (total < count) state.counts[selectedId] += count - total;
      for (const id of [...ids.filter((id) => id !== selectedId), selectedId]) {
        while (total > count && state.counts[id] > 0) {
          state.counts[id]--;
          total = Object.values(state.counts).reduce((sum, n) => sum + n, 0);
        }
      }
    }
    mixState.set(this.dataset.productId ?? '', state);
    return state;
  }

  /** @param {MouseEvent} event */
  #handleMixClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-mix-plus], [data-mix-minus]') : null;
    const row = button?.closest('[data-mix-row]');
    if (!(button instanceof HTMLButtonElement) || !(row instanceof HTMLElement) || button.disabled) return;

    const state = this.#mix();
    const id = row.dataset.variantId ?? '';
    const others = Object.keys(state.counts).filter((other) => other !== id);
    if (button.hasAttribute('data-mix-plus')) {
      // Take one unit from another variant so the pack size stays the same.
      const donor = [...others].reverse().find((other) => state.counts[other] > 0);
      if (!donor) return;
      state.counts[donor]--;
      state.counts[id]++;
    } else {
      if (state.counts[id] < 1 || !others.length) return;
      state.counts[id]--;
      state.counts[others[0]]++;
    }
    state.manual = true;
    this.#renderMix();
  };

  #renderMix() {
    const box = this.querySelector('[data-mix]');
    if (!(box instanceof HTMLElement)) return;
    const count = this.#count;
    box.hidden = count < 2;
    if (count < 2) {
      this.removeAttribute('data-mixed');
      return;
    }

    const { counts } = this.#mix();
    for (const row of this.#mixRows) {
      const id = row.dataset.variantId ?? '';
      const units = counts[id] ?? 0;
      const output = row.querySelector('[data-mix-count]');
      if (output) output.textContent = String(units);
      const minus = row.querySelector('[data-mix-minus]');
      const plus = row.querySelector('[data-mix-plus]');
      const unavailable = row.hasAttribute('data-unavailable');
      if (minus instanceof HTMLButtonElement) minus.disabled = unavailable || units < 1;
      if (plus instanceof HTMLButtonElement) plus.disabled = unavailable || units >= count;
    }
    this.toggleAttribute('data-mixed', Object.values(counts).filter((n) => n > 0).length > 1);
  }

  /** @param {number} cents */
  #money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat ?? '{{amount}}', this.dataset.currency ?? 'USD');
  }

  #apply = () => {
    const input = this.#selected;
    if (!input) return;
    const count = Number(input.value) || 1;
    const section = this.closest('.shopify-section') ?? document;

    // Selected variant × count in every product form of the section (main + sticky).
    for (const selector of section.querySelectorAll('quantity-selector-component')) {
      /** @type {any} */ (selector).setValue?.(String(count));
    }
    for (const quantity of section.querySelectorAll('product-form-component input[name="quantity"]')) {
      if (quantity instanceof HTMLInputElement) quantity.value = String(count);
    }
    this.dispatchEvent(new QuantitySelectorUpdateEvent(count));

    this.#renderMix();
    this.#renderPrice(section, Number(input.dataset.price), Number(input.dataset.compare), Number(input.dataset.discount));
  };

  /**
   * @param {ParentNode} section
   * @param {number} price
   * @param {number} compare
   * @param {number} discount
   */
  #renderPrice(section, price, compare, discount) {
    const containers = section.querySelectorAll('product-price [ref="priceContainer"], .sticky-add-to-cart__price [ref="priceContainer"]');
    const sale = compare > price;
    const escapeText = (value) => value.replace(/[&<>]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[character]));
    const saleLabel = escapeText(this.dataset.saleLabel ?? 'Sale price');
    const regularLabel = escapeText(this.dataset.regularLabel ?? 'Regular price');
    // Keep the sticky purchase bar consistent with the selected quantity and total.
    for (const container of containers) {
      container.innerHTML = sale
        ? `<span class="price-item__group price"><span class="visually-hidden">${saleLabel}&nbsp;</span><bdi>${this.#money(price)}</bdi></span>` +
          `<span class="price-item__group price-item--regular compare-at-price"><span class="visually-hidden">${regularLabel}&nbsp;</span><bdi>${this.#money(compare)}</bdi></span>` +
          (discount > 0 ? `<span class="price-item__discount">−${discount}%</span>` : '')
        : `<span class="price"><bdi>${this.#money(price)}</bdi></span>`;
    }
  }
}

if (!customElements.get('variant-pack-picker')) {
  customElements.define('variant-pack-picker', VariantPackPicker);
}
